import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Edit, Upload, X, Star, Save, FileText, Image as ImageIcon, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { api, API, loadAuthToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const FIELD_TYPES = [
  { value: "text", label: "Short text (छोटा उत्तर)" },
  { value: "textarea", label: "Long text (लंबा उत्तर)" },
  { value: "number", label: "Number (संख्या)" },
  { value: "date", label: "Date (तारीख़)" },
  { value: "dropdown", label: "Dropdown (चुनें)" },
  { value: "checkbox", label: "Checkbox (हाँ/नहीं)" },
  { value: "file", label: "File upload (फ़ाइल अपलोड)" },
];

const ACCEPT_LABEL = {
  photo: "Photo only (JPG/PNG)",
  pdf: "PDF only",
  photo_or_pdf: "Photo or PDF",
};

const empty = () => ({
  name_en: "", name_hi: "", desc_en: "", desc_hi: "",
  fee: "", required_docs: [], operator_instructions: "",
  form_schema: [], trending: false, public_visible: false, order: 0, active: true,
});

const AdminServicesPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!loadAuthToken()) { navigate("/admin/login"); return; }
    try { setMe(JSON.parse(localStorage.getItem("ap_admin_user") || "null")); } catch {}
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try { setItems((await api.get("/admin/services")).data || []); }
    catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login");
      else toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const isSuper = me?.role === "super_admin";

  const save = async () => {
    if (!editing.name_en || !editing.name_hi) {
      toast.error("Service name (English & Hindi) required"); return;
    }
    if (editing.trending && !editing.public_visible) {
      // Auto-enable public visibility when trending is enabled
      editing.public_visible = true;
    }
    setSaving(true);
    try {
      let saved;
      if (editing.id) {
        const { id, key, created_at, updated_at, template_file, photo_file, _pendingPhoto, ...body } = editing;
        saved = (await api.patch(`/admin/services/${editing.id}`, body)).data;
      } else {
        const { _pendingPhoto, ...body } = editing;
        saved = (await api.post("/admin/services", body)).data;
      }
      // If a new photo file selected, upload it now
      if (editing._pendingPhoto instanceof File) {
        const fd = new FormData(); fd.append("file", editing._pendingPhoto);
        await api.post(`/admin/services/${saved.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(editing.id ? "Service updated" : "Service created");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (svc) => {
    if (!window.confirm(`Delete "${svc.name_en}"?`)) return;
    try { await api.delete(`/admin/services/${svc.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const toggleTrending = async (svc) => {
    // Trending automatically enables public visibility
    const trending = !svc.trending;
    try { await api.patch(`/admin/services/${svc.id}`, { trending, public_visible: trending ? true : svc.public_visible }); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const togglePublic = async (svc) => {
    const public_visible = !svc.public_visible;
    try { await api.patch(`/admin/services/${svc.id}`, { public_visible, trending: !public_visible ? false : svc.trending }); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-services-page" className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-10">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} className="mb-2" data-testid="services-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-slate-900">Service Manager</h1>
          <p className="text-sm text-slate-500 mt-1">Add a service with photo, fee, required documents & form fields. Mark <b>Trending</b> to feature it on the homepage; unmarked services still appear on the Services page and Branch login.</p>
        </div>
        {isSuper && (
          <Button onClick={() => setEditing(empty())} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="new-service-btn">
            <Plus className="w-4 h-4 mr-2" /> New Service
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading && <div className="col-span-3 text-center text-slate-500 py-10">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="col-span-3 rounded-lg bg-white border-2 border-dashed border-slate-200 p-10 text-center text-slate-500">
            No services yet. Click <b>"New Service"</b> to add one.
          </div>
        )}
        {!loading && items.map((s) => (
          <div key={s.id} className="rounded-lg bg-white border border-slate-200 overflow-hidden flex flex-col" data-testid={`service-card-${s.key}`}>
            <div className="h-40 bg-slate-100 relative">
              {s.photo_file ? (
                <img src={`${API}/services/${s.key}/photo`} alt={s.name_en} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400"><ImageIcon className="w-8 h-8" /></div>
              )}
              <button
                onClick={() => toggleTrending(s)}
                className={`absolute top-2 right-2 rounded-full px-2 py-1 text-xs font-medium ${s.trending ? "bg-orange-500 text-white" : "bg-white/90 text-slate-700"}`}
                data-testid={`trending-toggle-${s.key}`}
              >
                <Star className="w-3 h-3 inline mr-0.5" /> {s.trending ? "Trending" : "Mark trending"}
              </button>
              <button
                onClick={() => togglePublic(s)}
                className={`absolute top-2 left-2 rounded-full px-2 py-1 text-xs font-medium ${s.public_visible ? "bg-green-600 text-white" : "bg-slate-700/80 text-white"}`}
                data-testid={`public-toggle-${s.key}`}
                title={s.public_visible ? "Visible to everyone" : "Branch operators only"}
              >
                {s.public_visible ? "🌍 Public" : "🔒 Branch only"}
              </button>
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <h3 className="font-semibold text-slate-900">{s.name_en}</h3>
              <div className="text-xs text-slate-500">{s.name_hi}</div>
              {s.fee && <div className="mt-1 text-sm text-teal-700 font-medium">{s.fee}</div>}
              <div className="mt-2 text-xs text-slate-600 line-clamp-2">{s.desc_en}</div>
              <div className="mt-3 flex flex-wrap gap-1 text-xs">
                {(s.required_docs || []).length > 0 && <Badge className="bg-slate-100 text-slate-700">{s.required_docs.length} docs</Badge>}
                {(s.form_schema || []).length > 0 && <Badge className="bg-blue-100 text-blue-800">{s.form_schema.length} fields</Badge>}
                {s.active === false && <Badge className="bg-slate-100 text-slate-500">Inactive</Badge>}
              </div>
              {isSuper && (
                <div className="mt-3 flex gap-2 pt-3 border-t border-slate-100">
                  <button className="text-teal-700 hover:underline text-sm font-medium flex items-center gap-1" onClick={() => setEditing(s)} data-testid={`edit-${s.key}`}><Edit className="w-3.5 h-3.5" />Edit</button>
                  <button className="text-red-600 hover:underline text-sm font-medium ml-auto flex items-center gap-1" onClick={() => del(s)} data-testid={`delete-${s.key}`}><Trash2 className="w-3.5 h-3.5" />Delete</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <ServiceEditor svc={editing} onChange={setEditing} onClose={() => setEditing(null)} onSave={save} saving={saving} />
    </div>
  );
};

const ServiceEditor = ({ svc, onChange, onClose, onSave, saving }) => {
  const photoRef = useRef(null);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!svc) return;
    if (svc._pendingPhoto instanceof File) {
      const url = URL.createObjectURL(svc._pendingPhoto);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    if (svc.photo_file && svc.key) setPreview(`${API}/services/${svc.key}/photo`);
    else setPreview(null);
  }, [svc]);

  if (!svc) return null;
  const set = (k, v) => onChange({ ...svc, [k]: v });
  const setField = (i, patch) => {
    const arr = [...svc.form_schema];
    arr[i] = { ...arr[i], ...patch };
    onChange({ ...svc, form_schema: arr });
  };
  const addField = () => onChange({
    ...svc,
    form_schema: [
      ...(svc.form_schema || []),
      { key: `field_${(svc.form_schema || []).length + 1}`, type: "text", label_en: "", label_hi: "", required: false, options: [], condition_field: "", condition_equals: "", accept: "photo_or_pdf" },
    ],
  });
  const removeField = (i) => onChange({ ...svc, form_schema: svc.form_schema.filter((_, x) => x !== i) });
  const move = (i, dir) => {
    const arr = [...svc.form_schema];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange({ ...svc, form_schema: arr });
  };

  return (
    <Dialog open={!!svc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="service-editor-dialog">
        <DialogHeader>
          <DialogTitle>{svc.id ? `Edit — ${svc.name_en}` : "New Service"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 text-sm">
          {/* Photo */}
          <div>
            <Label className="text-sm font-semibold">Main Photo</Label>
            <div className="mt-2 flex items-start gap-3">
              <div className="w-40 h-28 rounded-md bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center">
                {preview ? <img src={preview} alt="preview" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-slate-400" />}
              </div>
              <div className="flex-1">
                <Button variant="outline" size="sm" onClick={() => photoRef.current?.click()} data-testid="svc-photo-btn"><Upload className="w-4 h-4 mr-1" /> {preview ? "Change photo" : "Upload photo"}</Button>
                <input ref={photoRef} type="file" accept=".jpg,.jpeg,.png,.webp,image/*" className="hidden" onChange={(e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  if (f.size > 8 * 1024 * 1024) { toast.error("Max 8MB"); return; }
                  set("_pendingPhoto", f);
                }} />
                <p className="mt-1 text-xs text-slate-500">JPG / PNG / WEBP, max 8MB. Shown on home & submit form.</p>
              </div>
            </div>
          </div>

          {/* Basic */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1"><Label>Service Name (English) *</Label><Input value={svc.name_en} onChange={(e) => set("name_en", e.target.value)} placeholder="Passport Renewal" data-testid="svc-name-en" /></div>
            <div className="col-span-2 sm:col-span-1"><Label>सेवा का नाम (हिंदी) *</Label><Input value={svc.name_hi} onChange={(e) => set("name_hi", e.target.value)} placeholder="पासपोर्ट रिन्यूअल" data-testid="svc-name-hi" /></div>
            <div className="col-span-2"><Label>Description (English)</Label><Textarea rows={2} value={svc.desc_en || ""} onChange={(e) => set("desc_en", e.target.value)} placeholder="Short one-line description" /></div>
            <div className="col-span-2"><Label>विवरण (हिंदी)</Label><Textarea rows={2} value={svc.desc_hi || ""} onChange={(e) => set("desc_hi", e.target.value)} placeholder="छोटा विवरण एक-दो लाइन में" /></div>
            <div><Label>Fee</Label><Input value={svc.fee || ""} onChange={(e) => set("fee", e.target.value)} placeholder="₹200" /></div>
            <div><Label>Display order</Label><Input type="number" value={svc.order || 0} onChange={(e) => set("order", parseInt(e.target.value) || 0)} /></div>
            <div className="col-span-2 flex gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!svc.public_visible} onChange={(e) => set("public_visible", e.target.checked)} data-testid="svc-public-visible" /> <span><b>Show to Public</b> — website Services page + submit form. Unchecked = visible only to branch operators.</span></label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!svc.trending} onChange={(e) => set("trending", e.target.checked)} data-testid="svc-trending" /> Highlight on Homepage (Trending). Auto-enables "Show to Public".</label>
              {svc.id && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={svc.active !== false} onChange={(e) => set("active", e.target.checked)} /> Active</label>}
            </div>
          </div>

          {/* Required docs */}
          <div>
            <Label>Required Documents Checklist</Label>
            <p className="text-xs text-slate-500 mb-1">One document per line — shown to customers as "Please bring…"</p>
            <Textarea rows={3} value={(svc.required_docs || []).join("\n")} onChange={(e) => set("required_docs", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} placeholder="Aadhaar copy&#10;Old passport&#10;2 photos" data-testid="svc-required-docs" />
          </div>

          {/* Operator instructions */}
          <div>
            <Label>Operator Instructions (internal)</Label>
            <Textarea rows={2} value={svc.operator_instructions || ""} onChange={(e) => set("operator_instructions", e.target.value)} placeholder="Notes only visible to central office team" />
          </div>

          {/* Google Forms style field builder */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-semibold text-slate-800">Form Questions</div>
                <div className="text-xs text-slate-500">Yeh sawaal customer se bhare jayenge — Google Form ki tarah, jitne chahe add karein.</div>
              </div>
              <Button size="sm" onClick={addField} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="add-field-btn"><Plus className="w-3 h-3 mr-1" /> Add Question</Button>
            </div>

            {(svc.form_schema || []).length === 0 && <div className="text-xs text-slate-500 italic px-3 py-2 rounded bg-slate-50 border border-slate-200">Koi question nahi jodha. "Add Question" par tap karke shuru karein.</div>}

            <div className="space-y-3">
              {(svc.form_schema || []).map((f, i) => (
                <div key={i} className="rounded-lg border border-slate-200 bg-white p-4 relative" data-testid={`field-${i}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <GripVertical className="w-4 h-4 text-slate-400" />
                    <span className="text-xs font-mono text-slate-500">Q{i + 1}</span>
                    <button className="ml-auto text-slate-500 disabled:opacity-30" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp className="w-4 h-4" /></button>
                    <button className="text-slate-500 disabled:opacity-30" onClick={() => move(i, +1)} disabled={i === svc.form_schema.length - 1}><ArrowDown className="w-4 h-4" /></button>
                    <button onClick={() => removeField(i)} className="text-red-600 ml-2"><X className="w-4 h-4" /></button>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="col-span-2 sm:col-span-1">
                      <Label className="text-xs">Question (English)</Label>
                      <Input value={f.label_en} onChange={(e) => setField(i, { label_en: e.target.value })} placeholder="e.g. Passport Type" />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <Label className="text-xs">प्रश्न (हिंदी)</Label>
                      <Input value={f.label_hi || ""} onChange={(e) => setField(i, { label_hi: e.target.value })} placeholder="जैसे पासपोर्ट प्रकार" />
                    </div>
                    <div>
                      <Label className="text-xs">Answer Type</Label>
                      <Select value={f.type} onValueChange={(v) => setField(i, { type: v })}>
                        <SelectTrigger data-testid={`field-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Field ID (auto)</Label>
                      <Input value={f.key} onChange={(e) => setField(i, { key: e.target.value.replace(/[^a-z0-9_]/gi, "_") })} className="font-mono" />
                    </div>

                    {f.type === "file" && (
                      <div className="col-span-2">
                        <Label className="text-xs">Accept file type</Label>
                        <Select value={f.accept || "photo_or_pdf"} onValueChange={(v) => setField(i, { accept: v })}>
                          <SelectTrigger data-testid={`field-accept-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="photo">{ACCEPT_LABEL.photo}</SelectItem>
                            <SelectItem value="pdf">{ACCEPT_LABEL.pdf}</SelectItem>
                            <SelectItem value="photo_or_pdf">{ACCEPT_LABEL.photo_or_pdf}</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="mt-1 text-xs text-slate-500">Customer ke phone me sirf yeh file type khulega. "Photo only" → sidha camera/JPG allow hoga.</p>
                      </div>
                    )}

                    {f.type === "dropdown" && (
                      <div className="col-span-2">
                        <Label className="text-xs">Dropdown options (one per line)</Label>
                        <Textarea rows={2} value={(f.options || []).join("\n")} onChange={(e) => setField(i, { options: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} placeholder="New&#10;Renewal" />
                      </div>
                    )}

                    <div className="col-span-2 border-t pt-2 mt-1">
                      <div className="text-xs text-slate-500 mb-1">Show this question ONLY IF another dropdown answers a specific value (optional)</div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs">Depends on field ID</Label><Input value={f.condition_field || ""} onChange={(e) => setField(i, { condition_field: e.target.value })} placeholder="passport_type" /></div>
                        <div><Label className="text-xs">…equals</Label><Input value={f.condition_equals || ""} onChange={(e) => setField(i, { condition_equals: e.target.value })} placeholder="Renewal" /></div>
                      </div>
                    </div>

                    <label className="col-span-2 inline-flex items-center gap-2 pt-1"><input type="checkbox" checked={!!f.required} onChange={(e) => setField(i, { required: e.target.checked })} /> Required (jaruri)</label>
                  </div>
                </div>
              ))}
            </div>

            {(svc.form_schema || []).length > 0 && (
              <Button size="sm" variant="outline" onClick={addField} className="mt-3" data-testid="add-field-btn-bottom"><Plus className="w-3 h-3 mr-1" /> Add another question</Button>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="save-service-btn"><Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Service"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminServicesPage;
